import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useToast } from '../../components/common/Toast';
import { getArabicDate } from '../../services/utils/date';
import teacherService from '../../services/api/teacher';
import adminService from '../../services/api/admin';
import LoadingSpinner from '../../components/common/LoadingSpinner';

import { 
  Image as ImageIcon, 
  Upload, 
  X, 
  Save, 
  ArrowRight, 
  Layout, 
  Type, 
  FileText, 
  Eye, 
  ChevronRight,
  AlertCircle,
  Settings,
  Calendar,
  Layers,
  Sparkles,
  CheckCircle2,
  Trash2,
  Plus,
  Globe,
  Star,
  XCircle,
  EyeOff
} from 'lucide-react';

// Dynamic description function for course settings
const getCourseSettingDescription = (setting, value) => {
  const descriptions = {
    isPublicListed: {
      true: 'ظاهر في القوائم العامة للطلاب',
      false: 'مخفي عن القوائم العامة'
    },
    isFeatured: {
      true: 'معروض في قسم الكورسات المميزة',
      false: 'كورس عادي'
    },
    isActive: {
      true: 'متاح للطلاب حالياً',
      false: 'غير متاح مؤقتاً'
    }
  };
  
  return descriptions[setting]?.[value] || '';
};

// Enhanced setting titles with icons
const getCourseSettingTitle = (setting, value) => {
  const titles = {
    isPublicListed: {
      true: 'كورس عام',
      false: 'كورس خاص'
    },
    isFeatured: {
      true: 'كورس مميز',
      false: 'كورس عادي'
    },
    isActive: {
      true: 'كورس نشط',
      false: 'كورس معطل'
    }
  };
  
  return titles[setting]?.[value] || '';
};

const EditCourse = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // التحقق من وضع الإدارة
  const isAdminMode = useMemo(() => searchParams.get('mode') === 'admin', [searchParams]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    teacherSubjectId: '',
    isPublicListed: true,
    order: 0,
    price: 0,
    discountPrice: '',
    discountEndDate: '',
    durationHours: '',
    durationMinutes: '',
    defaultDurationDays: 30,
    isFeatured: false,
    isActive: true,
    isArchived: false
  });
  
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Learning Outcomes and Requirements
  const [learningOutcomes, setLearningOutcomes] = useState(['']);
  const [requirements, setRequirements] = useState(['']);

  // استقبال بيانات الدرس من صفحة دروسي إذا وجدت
  useEffect(() => {
    if (location.state?.lesson) {
      setFormData({
        title: location.state.lesson.title || '',
        description: location.state.lesson.description || '',
        teacherSubjectId: location.state.lesson.course_id || '',
        isPublicListed: true,
        order: 0
      });
    }
  }, [location.state]);

  // جلب بيانات الكورس الحالية
  const { data: course, isLoading, error } = useQuery(
    ['course', courseId, isAdminMode],
    () => {
      return isAdminMode ? adminService.getCourse(courseId) : teacherService.getTeacherCourse(courseId);
    },
    {
      enabled: !!courseId && courseId !== 'undefined',
      onSuccess: (data) => {
        setFormData({
          title: data.title || '',
          description: data.description || '',
          teacherSubjectId: data.teacher_subject_id || '',
          isPublicListed: data.is_public_listed || false,
          order: data.order || 0,
          price: data.price || '',
          discountPrice: data.discount_price || '',
          discountEndDate: data.discount_end_date ? new Date(data.discount_end_date).toISOString().split('T')[0] : '',
          durationHours: data.duration_hours || 0,
          durationMinutes: data.duration_minutes || 0,
          defaultDurationDays: data.default_duration_days || 30,
          isFeatured: data.is_featured || false,
          isActive: data.is_active !== false, // Default to true
          isArchived: data.is_archived || false
        });
        if (data.thumbnail) {
          // ✅ Fetch secure course thumbnail URL using the dedicated endpoint
          teacherService.getCourseImageUrl(courseId)
            .then(imageResponse => {
              if (imageResponse?.data?.imageUrl) {
                setThumbnailPreview(imageResponse.data.imageUrl);
              }
            })
            .catch(error => {
              // Fallback: if fetching secure URL fails, don't display broken image
              console.warn('Failed to fetch secure course thumbnail URL:', error);
            });
        }
      },
      onError: (error) => {
        toast.error('فشل في تحميل بيانات الكورس');
        navigate('/teacher/courses');
      }
    }
  );

  // جلب المواد الدراسية - تختلف حسب الوضع (معلم أو إدارة)
  const { data: subjects } = useQuery(
    ['subjects', isAdminMode],
    () => isAdminMode ? adminService.getSubjects() : teacherService.getSubjects()
  );

  // تحديث الكورس (Mutation)
  const updateCourseMutation = useMutation(
    (updatedData) => isAdminMode ? adminService.updateContent('courses', courseId, updatedData) : teacherService.updateCourse(courseId, updatedData),
    {
      onSuccess: () => {
        toast.success('تم تحديث الكورس بنجاح!');
        queryClient.invalidateQueries(isAdminMode ? ['admin-content', 'courses'] : 'teacher-courses');
        queryClient.invalidateQueries(['course', courseId]);
        navigate(isAdminMode ? '/admin/content' : '/teacher/courses');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في تحديث الكورس');
      },
      onSettled: () => setIsSubmitting(false)
    }
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Helper functions for learning outcomes and requirements
  const addLearningOutcome = () => {
    setLearningOutcomes([...learningOutcomes, '']);
  };
  
  const removeLearningOutcome = (index) => {
    setLearningOutcomes(learningOutcomes.filter((_, i) => i !== index));
  };
  
  const updateLearningOutcome = (index, value) => {
    const updated = [...learningOutcomes];
    updated[index] = value;
    setLearningOutcomes(updated);
  };
  
  const addRequirement = () => {
    setRequirements([...requirements, '']);
  };
  
  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };
  
  const updateRequirement = (index, value) => {
    const updated = [...requirements];
    updated[index] = value;
    setRequirements(updated);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
if (file.size > 50 * 1024 * 1024) {
      toast.error('حجم الصورة كبير جداً (الأقصى 50 ميجابايت)');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('يجب اختيار ملف صورة صالح');
        return;
      }

      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('يرجى إدخال عنوان الكورس');
      return;
    }

    if (!formData.teacherSubjectId) {
      toast.error('يرجى اختيار المادة الدراسية');
      return;
    }

    setIsSubmitting(true);
    
    // إنشاء FormData لدعم رفع الصور (المنطق الأصلي كاملاً)
    const courseFormData = new FormData();
    courseFormData.append('title', formData.title);
    courseFormData.append('description', formData.description);
    courseFormData.append('teacherSubjectId', String(formData.teacherSubjectId));
    
    // booleans - explicit string conversion
    courseFormData.append('isPublicListed', String(formData.isPublicListed));
    courseFormData.append('isFeatured', String(formData.isFeatured));
    courseFormData.append('isActive', String(formData.isActive));
    courseFormData.append('isArchived', String(formData.isArchived));
    
    // numbers - price is required, others are optional
    courseFormData.append('price', String(formData.price));
    
    if (formData.order != null)
      courseFormData.append('order', String(formData.order));
    
    if (formData.discountPrice != null && formData.discountPrice !== '')
      courseFormData.append('discountPrice', String(formData.discountPrice));
    
    if (formData.durationHours != null && formData.durationHours !== '')
      courseFormData.append('durationHours', String(formData.durationHours));
    
    if (formData.durationMinutes != null && formData.durationMinutes !== '')
      courseFormData.append('durationMinutes', String(formData.durationMinutes));
    
    if (formData.defaultDurationDays != null)
      courseFormData.append('defaultDurationDays', String(formData.defaultDurationDays));
    
    // date - only if exists
    if (formData.discountEndDate) {
      courseFormData.append('discountEndDate', formData.discountEndDate);
    }
    
    // إضافة نتائج التعلم
    learningOutcomes.filter(outcome => outcome.trim()).forEach((outcome, index) => {
      courseFormData.append(`learningOutcomes[${index}]`, outcome);
    });
    
    // إضافة المتطلبات
    requirements.filter(requirement => requirement.trim() !== '').forEach((requirement, index) => {
      courseFormData.append(`requirements[${index}]`, requirement);
    });
    
    if (thumbnail) {
      courseFormData.append('thumbnail', thumbnail);
    }

    updateCourseMutation.mutate(courseFormData);
  };

  // معالجة حالات التحميل والخطأ (كما في الكود الأصلي)
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل بيانات الكورس...</p>
      </div>
    );
  }

  if (error || !courseId || courseId === 'undefined') {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">خطأ في معرف الكورس</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {error?.message || 'لم يتم العثور على الكورس المطلوب أو أن الرابط غير صالح.'}
          </p>
          <button
            onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/courses')}
            className="bg-primary-600 text-white px-10 py-3 rounded-2xl font-bold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg"
          >
            <ArrowRight size={18} /> العودة {isAdminMode ? 'لإدارة المحتوى' : 'للكورسات'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-10 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
                onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/courses')}
                className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                العودة {isAdminMode ? 'لإدارة المحتوى' : 'لإدارة الكورسات'}
            </button>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-blue-600 text-white p-1.5 rounded-xl"><Sparkles size={24} /></div>
                تعديل بيانات الكورس
            </h1>
        </div>
        <div className="flex items-center gap-3">
            <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-xs font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <Calendar size={14} className="text-primary-500" />
                تعديل أخير: {getArabicDate(course.updated_at || course.created_at)}
            </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10">
            <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                <Layout size={20} className="text-primary-600" />
                معلومات الكورس الأساسية
            </h2>

            <div className="space-y-6">
              {/* Title Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Type size={16} className="text-primary-500" /> عنوان الكورس الحالي *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الكورس الجديد"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  required
                />
              </div>

              {/* Subject Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Layers size={16} className="text-primary-500" /> المادة والصف الدراسي *
                </label>
                <div className="relative">
                    <select
                    id="teacherSubjectId"
                    name="teacherSubjectId"
                    value={formData.teacherSubjectId}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    required
                    >
                    <option value="">اختر المادة المناسبة</option>
                    {subjects?.map((subject) => (
                        <option key={subject.teacher_subject_id || subject.id} value={subject.teacher_subject_id || subject.id}>
                        {isAdminMode ? subject.name : `${subject.subject_name} - ${subject.grade_name}`}
                        </option>
                    ))}
                    </select>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <ChevronRight className="rotate-90" size={20} />
                    </div>
                </div>
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> وصف الكورس وتفاصيله
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="حدث وصف الكورس لطلابك..."
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed"
                />
                <p className="text-[10px] text-gray-400 font-bold px-2 text-left">{formData.description.length} حرف تم استخدامه</p>
              </div>

              {/* Default Duration Days Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> مدة الاشتراك الافتراضية (بالأيام)
                </label>
                <div className="relative">
                  <select
                    id="defaultDurationDays"
                    name="defaultDurationDays"
                    value={formData.defaultDurationDays}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 pr-16 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 appearance-none cursor-pointer"
                  >
                    <option value="">اختر مدة الاشتراك</option>
                    <option value={30}>30 يوم - اشتراك شهري</option>
                    <option value={120}>120 يوم - اشتراك ترم دراسي</option>
                    <option value={300}>300 يوم - اشتراك عام دراسي</option>
                  </select>
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <ChevronRight className="rotate-90" size={20} />
                  </div>
                </div>
                <p className="text-[10px] font-bold text-gray-400 mt-1 mr-2">
                  المدة الافتراضية لاشتراك الطالب في الكورس
                </p>
              </div>

              {/* Price Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> سعر الكورس (اختياري)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full px-5 py-4 pr-16 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    جنية
                  </div>
                </div>
                <p className="text-[10px] font-bold text-gray-400 mt-1 mr-2">
                  اتركه فارغاً إذا كان الكورس مجانياً
                </p>
              </div>

              {/* Discount Price Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> سعر بعد الخصم (اختياري)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="discountPrice"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full px-5 py-4 pr-16 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    جنية
                  </div>
                </div>
              </div>

              {/* Discount End Date Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> تاريخ انتهاء الخصم (اختياري)
                </label>
                <input
                  type="date"
                  id="discountEndDate"
                  name="discountEndDate"
                  value={formData.discountEndDate}
                  onChange={handleInputChange}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700"
                />
              </div>

              {/* Duration Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <CheckCircle2 size={16} className="text-primary-500" /> مدة (ساعات)
                  </label>
                  <input
                    type="number"
                    id="durationHours"
                    name="durationHours"
                    value={formData.durationHours}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="999"
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <CheckCircle2 size={16} className="text-primary-500" /> مدة (دقائق)
                  </label>
                  <input
                    type="number"
                    id="durationMinutes"
                    name="durationMinutes"
                    value={formData.durationMinutes}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="59"
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Settings Area */}
        <div className="space-y-6">

          {/* Thumbnail Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3">
                <ImageIcon size={18} className="text-primary-600" /> غلاف الكورس
            </h2>

            <div className="space-y-6">
              {thumbnailPreview ? (
                <div className="relative group rounded-3xl overflow-hidden shadow-xl ring-4 ring-gray-50 transition-all">
                  <img
                    src={thumbnailPreview}
                    alt="Preview"
                    className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={removeThumbnail}
                      className="bg-red-600 text-white p-3 rounded-2xl hover:bg-red-700 transition-all shadow-lg transform hover:scale-110"
                      title="حذف الصورة"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
              ) : (
                <label 
                  htmlFor="thumbnail"
                  className="w-full aspect-video border-4 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center group hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <div className="bg-primary-50 text-primary-600 p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform">
                    <Upload size={32} />
                  </div>
                  <span className="text-sm font-black text-gray-900">اضغط لرفع غلاف</span>
                  <span className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">JPG, PNG (Max 50MB)</span>
                </label>
              )}
              
              <input
                type="file"
                id="thumbnail"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="hidden"
              />
              
              {thumbnailPreview && (
                  <label htmlFor="thumbnail" className="w-full py-3 px-4 border border-gray-100 rounded-xl font-bold text-xs text-primary-600 bg-primary-50/50 hover:bg-primary-100 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                      <Upload size={14} /> تغيير صورة الغلاف
                  </label>
              )}
            </div>
          </div>

          {/* Learning Outcomes Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <CheckCircle2 size={20} className="text-primary-600" />
                أهداف التعلم
            </h2>
            <div className="space-y-4">
              <p className="text-[10px] font-bold text-gray-400">
                ماذا سيتعلم الطالب بعد هذا الكورس؟
              </p>
              {learningOutcomes.map((outcome, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={outcome}
                    onChange={(e) => updateLearningOutcome(index, e.target.value)}
                    placeholder="ماذا سيتعلم الطالب بعد هذا الكورس؟"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 placeholder:text-gray-400"
                  />
                  {learningOutcomes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLearningOutcome(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addLearningOutcome}
                className="w-full py-3 bg-primary-50 border-2 border-dashed border-primary-200 text-primary-600 rounded-xl hover:bg-primary-100 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <Plus size={16} />
                إضافة هدف تعلم جديد
              </button>
            </div>
          </div>

          {/* Requirements Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <AlertCircle size={20} className="text-primary-600" />
                المتطلبات
            </h2>
            <div className="space-y-4">
              <p className="text-[10px] font-bold text-gray-400">
                ماذا يجب أن يعرفه الطالب قبل هذا الكورس؟
              </p>
              {requirements.map((requirement, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={requirement}
                    onChange={(e) => updateRequirement(index, e.target.value)}
                    placeholder="ماذا يجب أن يعرفه الطالب قبل هذا الكورس؟"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 placeholder:text-gray-400"
                  />
                  {requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addRequirement}
                className="w-full py-3 bg-primary-50 border-2 border-dashed border-primary-200 text-primary-600 rounded-xl hover:bg-primary-100 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <Plus size={16} />
                إضافة متطلب جديد
              </button>
            </div>
          </div>

          {/* Visibility and Order Settings */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 space-y-6">
            <h2 className="text-lg font-black text-gray-900 flex items-center gap-3">
                <Settings size={18} className="text-primary-600" /> إعدادات الظهور
            </h2>

            {/* Public Listed Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isPublicListed ? <Globe size={14} className="text-blue-600" /> : <EyeOff size={14} className="text-gray-400" />}
                    {getCourseSettingTitle('isPublicListed', formData.isPublicListed)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isPublicListed', formData.isPublicListed)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isPublicListed"
                        name="isPublicListed"
                        checked={formData.isPublicListed}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
            </div>

            {/* Featured Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isFeatured ? <Star size={14} className="text-yellow-500" /> : <Star size={14} className="text-gray-400" />}
                    {getCourseSettingTitle('isFeatured', formData.isFeatured)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isFeatured', formData.isFeatured)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isFeatured"
                        name="isFeatured"
                        checked={formData.isFeatured}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
            </div>

            {/* Active Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isActive ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                    {getCourseSettingTitle('isActive', formData.isActive)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isActive', formData.isActive)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
            </div>

            {/* Order Input */}
            <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mr-2">ترتيب العرض (أولوية)</label>
                <div className="relative">
                    <input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                    />
                    <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                </div>
            </div>

            {/* Save Buttons */}
            <div className="pt-4 space-y-4">
                <button
                    type="submit"
                    disabled={updateCourseMutation.isLoading || isSubmitting}
                    className="w-full bg-primary-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 transform active:scale-95"
                >
                    <Save size={20} />
                    {updateCourseMutation.isLoading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/courses')}
                    className="w-full py-4 rounded-2xl font-bold text-gray-400 hover:bg-gray-50 transition-all text-sm border border-transparent hover:border-gray-100"
                >
                    إلغاء التعديل
                </button>
            </div>
          </div>

          {/* Quick Info Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10 space-y-3">
                <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center">
                    <Eye size={20} />
                </div>
                <h3 className="font-black text-lg">نصيحة التعديل</h3>
                <p className="text-gray-400 text-xs leading-relaxed font-medium">
                    تأكد من مراجعة عنوان الكورس ووصفه دورياً لإضافة الكلمات الدلالية التي يبحث عنها الطلاب، مما يزيد من فرص ظهور كورسـك في نتائج البحث.
                </p>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default EditCourse;